import React, { useCallback, useRef } from "react";
import { LABEL_PALETTE, resolveLabelColors } from "./CanvasLabelNode";

function LabelItem({ node, onUpdate, onDelete, onSelect, isSelected, onSendBackward, onBringForward, isFirst, isLast }) {
  const cl = node.data.canvasLabel;
  const colors = resolveLabelColors(cl.color);
  const ref = useRef(null);

  return (
    <div
      ref={ref}
      className={`labels-panel-item${isSelected ? " labels-panel-item--selected" : ""}`}
      onClick={() => onSelect(node.id)}
      style={{ borderLeft: `3px solid ${colors.border}` }}
    >
      <div className="labels-panel-item-header">
        <span className="labels-panel-item-label">{cl.label}</span>
        <div className="labels-panel-item-actions">
          <button
            className="labels-panel-sort-btn"
            title="Send backward"
            disabled={isFirst}
            onClick={(e) => { e.stopPropagation(); onSendBackward(node.id); }}
          >
            &darr;
          </button>
          <button
            className="labels-panel-sort-btn"
            title="Bring forward"
            disabled={isLast}
            onClick={(e) => { e.stopPropagation(); onBringForward(node.id); }}
          >
            &uarr;
          </button>
          <button
            className="labels-panel-delete-btn"
            title="Delete label"
            onClick={(e) => { e.stopPropagation(); onDelete(node.id); }}
          >
            &times;
          </button>
        </div>
      </div>
      {cl.description && (
        <div className="labels-panel-item-description">{cl.description}</div>
      )}
      <div className="labels-panel-color-swatches">
        {LABEL_PALETTE.map((colors_opt, index) => (
          <button
            key={index}
            className={`labels-panel-swatch${cl.color === index ? " labels-panel-swatch--active" : ""}`}
            style={{ backgroundColor: colors_opt.border }}
            onClick={(e) => { e.stopPropagation(); onUpdate(node.id, { color: index }); }}
          />
        ))}
      </div>
    </div>
  );
}

export default function LabelsPanel({ nodes, onUpdate, onSwapOrder, onDelete, onSelect, selectedLabelId }) {
  const labelNodes = nodes
    .filter((n) => n.type === "canvasLabel")
    .sort((a, b) => (b.data.canvasLabel.sort_order || 0) - (a.data.canvasLabel.sort_order || 0));

  const usedColors = new Set(labelNodes.map((n) => n.data.canvasLabel.color).filter((c) => typeof c === "number"));
  const nextColor = LABEL_PALETTE.findIndex((_, i) => !usedColors.has(i));
  const defaultColor = nextColor >= 0 ? nextColor : 0;

  // List is sorted front-to-back (descending sort_order), so the neighbor
  // below in the list has a lower sort_order (further back on canvas).
  const handleSendBackward = useCallback((nodeId) => {
    const idx = labelNodes.findIndex((n) => n.id === nodeId);
    if (idx < 0 || idx >= labelNodes.length - 1) return;
    const curr = labelNodes[idx];
    const below = labelNodes[idx + 1];
    let currOrder = curr.data.canvasLabel.sort_order ?? 0;
    let belowOrder = below.data.canvasLabel.sort_order ?? 0;
    if (currOrder === belowOrder) currOrder = belowOrder + 1;
    onSwapOrder(curr.id, belowOrder, below.id, currOrder);
  }, [labelNodes, onSwapOrder]);

  const handleBringForward = useCallback((nodeId) => {
    const idx = labelNodes.findIndex((n) => n.id === nodeId);
    if (idx <= 0) return;
    const curr = labelNodes[idx];
    const above = labelNodes[idx - 1];
    let currOrder = curr.data.canvasLabel.sort_order ?? 0;
    let aboveOrder = above.data.canvasLabel.sort_order ?? 0;
    if (currOrder === aboveOrder) aboveOrder = currOrder + 1;
    onSwapOrder(curr.id, aboveOrder, above.id, currOrder);
  }, [labelNodes, onSwapOrder]);

  const onDragStart = useCallback((event) => {
    event.dataTransfer.setData(
      "application/json",
      JSON.stringify({ canvasLabel: { label: "Label", color: defaultColor, sort_order: 0 } })
    );
    event.dataTransfer.effectAllowed = "move";
  }, [defaultColor]);

  return (
    <div className="labels-panel">
      <div className="labels-panel-drag-area">
        <div
          className="labels-panel-drag-item"
          draggable
          onDragStart={onDragStart}
        >
          <span className="labels-panel-drag-icon">&#9633;</span>
          Area Label
          <div className="labels-panel-drag-grip" aria-hidden="true" title="Drag to add">&#x2630;</div>
        </div>
      </div>
      {labelNodes.length === 0 ? (
        <div className="labels-panel-empty">
          Drag an area label onto the canvas to group devices visually.
        </div>
      ) : (
        <div className="labels-panel-list">
          {labelNodes.map((node, idx) => (
            <LabelItem
              key={node.id}
              node={node}
              onUpdate={onUpdate}
              onDelete={onDelete}
              onSelect={onSelect}
              isSelected={node.id === selectedLabelId}
              onSendBackward={handleSendBackward}
              onBringForward={handleBringForward}
              isFirst={idx === labelNodes.length - 1}
              isLast={idx === 0}
            />
          ))}
        </div>
      )}
    </div>
  );
}
